import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';

import { PrettyUrlsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { AdaptiveLayoutState } from '@frontend/sports/layout/feature';
import { MyBetsUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { OpenBetsSummary } from '@frontend/sports/types/models/my-bets';
import { DeviceService } from '@frontend/vanilla/core';

import { MyBetsSummaryService } from '../../my-bets-base/my-bets-summary.service';
import { SubNavigationResolve } from '../../sub-navigation/sub-navigation-resolve.service';
import { SubNavigationItem } from '../../sub-navigation/sub-navigation.models';

@Injectable()
export class MyBetsSubnavigationResolveService extends SubNavigationResolve<any> {
    private tab: string;
    private tabs: SubNavigationItem[];
    private lastActiveTabUrl: string;
    private lastMyBetsStats: OpenBetsSummary;
    private myBetTabStyle = 'mybets-tab-bar-item';
    private myBetTabWithCountStyle = 'mybets-tab-bar-item-count';
    readonly isMobileMode: boolean;

    constructor(
        private urlHelper: UrlHelperService,
        private sitecoreMyBets: SitecoreMyBetsConfig,
        private mybetsSummary: MyBetsSummaryService,
        private urlConfig: PrettyUrlsConfig,
        private device: DeviceService,
    ) {
        super();
        this.mybetsSummary.summaryUpdate.pipe(takeUntilDestroyed()).subscribe((summary) => this.tabs$.next(this.getDefaultTabs(summary)));
        this.isMobileMode = this.device.isMobile;
    }

    buildSubNavigation(layout: AdaptiveLayoutState, routeSnapshot: ActivatedRouteSnapshot): void {
        this.tab = routeSnapshot.params && routeSnapshot.params.tab ? routeSnapshot.params.tab : null;
        this.tabs$.next(this.getDefaultTabs(this.mybetsSummary.summary));
        if (this.isMobileMode) {
            this.fullWidth$.next(true);
        }
    }

    build(summary: OpenBetsSummary): void {
        this.tabs$.next(this.getDefaultTabs(summary));
    }

    private getDefaultTabs(count: OpenBetsSummary): SubNavigationItem[] {
        if (this.lastActiveTabNotEmitted() || this.hasBetsStatsChanged(count)) {
            this.tabs = [
                {
                    title: this.sitecoreMyBets.myBets.Live || 'Live',
                    url: this.urlHelper.getMyBetsUrl(MyBetsUrlParam.Live),
                    active: this.tab === this.urlConfig.translations.live,
                    count: count.liveBetsCount || undefined,
                    classes: count.liveBetsCount ? this.myBetTabWithCountStyle : this.myBetTabStyle,
                    id: 0,
                    ignoreBrowserHistory: true,
                    attribute: 'mybets-tab-live',
                },
                {
                    title: this.sitecoreMyBets.myBets.OPEN || 'Open',
                    url: this.urlHelper.getMyBetsUrl(MyBetsUrlParam.Open),
                    active: this.tab === this.urlConfig.translations.open,
                    count: count.openBetsCount || undefined,
                    classes: count.openBetsCount ? this.myBetTabWithCountStyle : this.myBetTabStyle,
                    id: 1,
                    ignoreBrowserHistory: true,
                    attribute: 'mybets-tab-open',
                },
                {
                    title: this.sitecoreMyBets.myBets.Settled || 'Settled',
                    url: this.urlHelper.getMyBetsUrl(MyBetsUrlParam.Settled),
                    active: this.tab === this.urlConfig.translations.settled,
                    classes: this.myBetTabStyle,
                    id: 2,
                    ignoreBrowserHistory: true,
                    attribute: 'mybets-tab-settled',
                },
            ];

            this.lastActiveTabUrl = this.tabs.find((tab) => tab.active === true)?.url?.toLowerCase() || '';
            this.lastMyBetsStats = count;
        }

        return this.tabs;
    }

    private lastActiveTabNotEmitted(): boolean {
        return (
            !this.lastActiveTabUrl || this.lastActiveTabUrl.indexOf(this.tab) < 0 // when false means initial load
        ); // when switching tabs the subscription emits the previous active tab and switches back to it
    }

    private hasBetsStatsChanged(myBetsCount: OpenBetsSummary): boolean {
        return this.lastMyBetsStats
            ? this.lastMyBetsStats.liveBetsCount !== myBetsCount.liveBetsCount || this.lastMyBetsStats.openBetsCount !== myBetsCount.openBetsCount
            : false;
    }
}
